import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await axios.get(`${API}/testimonials`);
        setTestimonials(res.data);
      } catch (e) {
        console.error('Error fetching testimonials:', e);
      }
    };
    fetchTestimonials();
  }, []);

  useEffect(() => {
    if (!isAutoPlaying || testimonials.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const goTo = (idx) => {
    setCurrentIndex(idx);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const prev = () => goTo((currentIndex - 1 + testimonials.length) % testimonials.length);
  const next = () => goTo((currentIndex + 1) % testimonials.length);

  const getVisibleTestimonials = () => {
    if (testimonials.length === 0) return [];
    const items = [];
    for (let i = -1; i <= 1; i++) {
      const idx = (currentIndex + i + testimonials.length) % testimonials.length;
      items.push({ ...testimonials[idx], position: i });
    }
    return items;
  };

  if (testimonials.length === 0) return null;

  return (
    <section className="py-16 md:py-20 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-center gap-1 mb-6">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={24} className="text-[#FFB800] fill-[#FFB800]" />
          ))}
        </div>
        <div className="relative">
          <div className="flex items-center justify-center">
            <button
              onClick={prev}
              className="absolute left-0 z-10 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors duration-200 border border-gray-200"
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </button>
            <div className="flex items-center justify-center gap-6 mx-14 overflow-hidden">
              {getVisibleTestimonials().map((testimonial, idx) => (
                <div
                  key={`${testimonial.id}-${idx}`}
                  className={`transition-all duration-500 ease-in-out flex-shrink-0 ${
                    testimonial.position === 0
                      ? 'opacity-100 scale-100'
                      : 'opacity-40 scale-90 hidden md:block'
                  }`}
                >
                  <div className="bg-white rounded-2xl p-8 max-w-md text-center">
                    <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-4">
                      <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" />
                    </div>
                    <blockquote className="text-gray-800 text-lg md:text-xl font-semibold leading-relaxed mb-4">
                      "{testimonial.text}"
                    </blockquote>
                    <p className="text-gray-500 text-sm font-medium">{testimonial.name}</p>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={next}
              className="absolute right-0 z-10 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors duration-200 border border-gray-200"
            >
              <ChevronRight size={20} className="text-gray-600" />
            </button>
          </div>
          <div className="flex items-center justify-center gap-2 mt-8">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goTo(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  idx === currentIndex ? 'bg-[#37b34a] w-6' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
